import { useId, type ComponentPropsWithoutRef, type Ref } from 'react';

import { cn } from '@/lib/utils/cn';

import { FIELD_INPUT, FIELD_LABEL, FIELD_STACK, fieldShell } from './field-shell';

interface TextareaProps extends Omit<ComponentPropsWithoutRef<'textarea'>, 'id'> {
  label: string;
  error?: string | undefined;
  hint?: string;
  ref?: Ref<HTMLTextAreaElement>;
}

/**
 * Zone de texte, habillée comme les champs d'une ligne.
 *
 * `resize-y` et non `resize` : l'utilisateur peut agrandir une description
 * longue, mais pas élargir le champ au-delà de sa colonne — un redimensionnement
 * horizontal casse la grille du formulaire.
 */
export const Textarea = ({
  label,
  error,
  hint,
  className,
  rows = 5,
  ref,
  disabled,
  ...rest
}: TextareaProps) => {
  const id = useId();
  const messageId = `${id}-message`;
  const hasError = Boolean(error);

  return (
    <div className={FIELD_STACK}>
      <label htmlFor={id} className={FIELD_LABEL}>
        {label}
      </label>

      {/* `items-stretch` : sans lui, l'habillage centre la zone de texte
          verticalement et laisse deux bandes vides au-dessus et en dessous. */}
      <div className={cn(fieldShell({ hasError, disabled: disabled ?? false }), 'items-stretch')}>
        <textarea
          id={id}
          ref={ref}
          rows={rows}
          disabled={disabled}
          aria-invalid={hasError}
          aria-describedby={(error ?? hint) ? messageId : undefined}
          className={cn(FIELD_INPUT, 'resize-y rounded-lg px-4 py-3 leading-6', className)}
          {...rest}
        />
      </div>

      {error ? (
        <p id={messageId} role="alert" className="text-xs leading-5 text-red-600">
          {error}
        </p>
      ) : hint ? (
        <p id={messageId} className="text-ink-500 text-xs leading-5">
          {hint}
        </p>
      ) : null}
    </div>
  );
};
